import type { ComponentProps } from "react";
import { PageBody } from "../PageBody";
import styled from "styled-components";
import { hexToRGB } from "../../../utils";
import { Typography } from "../../atoms/Typography";
import { List, ListItem } from "../../atoms/List";
import { StatusLabelMap } from "../../../utils/prescription";
import { Link } from "react-router";

const ContentWrapper = styled.div`
  padding: 0 40px;
`;

const Inner = styled.div`
  padding: 40px 0 20px;
  display: flex;
  ${(props) => hexToRGB(props.theme.colors.primaryBorder, 0.5)};
`;

const LeftBlock = styled.div`
  flex: 1;
  padding-right: 40px;
`;

const MemberIconAndName = styled.div`
  display: flex;
  align-items: center;
  img {
    margin-right: 20px;
  }
`;

const RightBlock = styled.div`
  flex: 1;
  padding-left: 40px;
`;

const PaddingRightWrapper = styled.div`
  width: 100%;
  padding-right: 20px;
`;

const StyledLink = styled(Link)`
  color: ${(props) => props.theme.colors.brand};
`;

const TextRightTypography = (props: ComponentProps<typeof Typography>) => (
  <PaddingRightWrapper>
    <Typography textRight size={2}>
      {props.children}
    </Typography>
  </PaddingRightWrapper>
);

type PatientHaudy = {
  id: string;
  prescriptionCode: string;
  prescribedAt: string;
  status: string;
  nickname: string;
  name: string;
  memberImage: any;
  birthMonth: string;
  appName: string;
  medicalInstitutionId: string;
  medicalInstitutionName: string;
  firstConsultationAt: string;
  latestConsultationAt: string;
  drinkingRecordRate: string;
};

export type PatientHaudyDetailPageProps = {
  patientHaudy: PatientHaudy;
};

export function PatientHaudyDetailPage({
  patientHaudy,
}: PatientHaudyDetailPageProps) {
  return (
    <>
      <PageBody title="処方詳細">
        <ContentWrapper>
          <Inner>
            <LeftBlock>
              <MemberIconAndName>
                <img
                  src={patientHaudy.memberImage}
                  width="72"
                  height="72"
                  alt={"user_icon"}
                />
                <List>
                  <ListItem>
                    <Typography bold size={1}>
                      ニックネーム
                    </Typography>
                    <Typography size={5}>{patientHaudy.nickname}</Typography>
                  </ListItem>
                  <ListItem>
                    <Typography bold size={1}>
                      氏名
                    </Typography>
                    <Typography size={3}>{patientHaudy.name}</Typography>
                  </ListItem>
                </List>
              </MemberIconAndName>
            </LeftBlock>
          </Inner>
        </ContentWrapper>

        <ContentWrapper>
          <Inner>
            <LeftBlock>
              <List>
                <ListItem>
                  <Typography bold size={1}>
                    処方日
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.prescribedAt}
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    アプリ名
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.appName}
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    初回診察日
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.firstConsultationAt}
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    最新診察日
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.latestConsultationAt}
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    飲酒記録の記録率
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.drinkingRecordRate}
                  </TextRightTypography>
                </ListItem>
              </List>
            </LeftBlock>
            <RightBlock>
              <List>
                <ListItem>
                  <Typography bold size={1}>
                    生年月
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.birthMonth}
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    処方コード
                  </Typography>
                  <TextRightTypography>
                    {patientHaudy.prescriptionCode}
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    処方医療機関
                  </Typography>
                  <TextRightTypography>
                    <StyledLink
                      to={`/medicalInstitution/${patientHaudy.medicalInstitutionId}`}
                    >
                      {patientHaudy.medicalInstitutionName}
                    </StyledLink>
                  </TextRightTypography>
                </ListItem>
                <ListItem>
                  <Typography bold size={1}>
                    処方コードステータス
                  </Typography>
                  <TextRightTypography>
                    {
                      StatusLabelMap[
                        patientHaudy.status as keyof typeof StatusLabelMap
                      ]
                    }
                  </TextRightTypography>
                </ListItem>
              </List>
            </RightBlock>
          </Inner>
        </ContentWrapper>
      </PageBody>
    </>
  );
}
